import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";

import { DateTimeInput } from "@/components/inputs/DateTimeInput";
import { PlayerIdInput } from "@/components/inputs/PlayerIdInput";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { TrialInput, zTrialInput } from "./validation/trial";

export const TrialForm = ({
    onSubmit,
    defaultValues,
    onDelete,
}: {
    onSubmit: SubmitHandler<TrialInput>;
    defaultValues: TrialInput;
    onDelete?: () => void;
}) => {
    const form = useForm<TrialInput>({
        defaultValues: defaultValues,
        mode: "onBlur",
        resolver: zodResolver(zTrialInput),
    });

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="player1Id"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Player 1</FormLabel>
                            <FormControl>
                                <PlayerIdInput
                                    className="max-w-[500px] flex-1"
                                    playerId={field.value}
                                    onPlayerIdChange={field.onChange}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="player2Id"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Player 2</FormLabel>
                            <FormControl>
                                <PlayerIdInput
                                    className="max-w-[500px] flex-1"
                                    playerId={field.value}
                                    onPlayerIdChange={field.onChange}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="result"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Result</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value ?? undefined}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a trial result" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="PLAYER_1_WIN">Player 1 win</SelectItem>
                                    <SelectItem value="PLAYER_2_WIN">Player 2 win</SelectItem>
                                    <SelectItem value="DRAW">Draw</SelectItem>
                                    <SelectItem value="ABORTED">Aborted</SelectItem>
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="finishedAt"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Finished at</FormLabel>
                            <FormControl>
                                <DateTimeInput value={field.value} onValue={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className="mt-4 flex justify-between gap-2">
                    <Button
                        type="submit"
                        disabled={!form.formState.isValid || form.formState.isSubmitting}
                    >
                        Save
                    </Button>
                    {onDelete && (
                        <Button
                            type="button"
                            variant="outline"
                            className="border-red-600 text-red-600"
                            onClick={onDelete}
                        >
                            Delete trial
                        </Button>
                    )}
                </div>
            </form>
        </Form>
    );
};
